import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { AccessDenied } from '@/components/permission-guard'
import { useModal } from '@/components/modals'
import { useAuth } from '@/lib/auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { apiErrorDetail } from '@/lib/api/fraud-client'
import { useCreateFraudAllowance, useFraudUser } from '@/lib/api/fraud-queries'
import { useEvent } from '@/lib/api/queries'
import { usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { BlockForm, CardBlockForm, LiftButton } from '@/features/fraud/blocks-tab'
import type { FraudCardOnFile } from '@/lib/api/fraud-types'
import { RevokeButton, TrustForm } from '@/features/fraud/trusted-tab'
import { Banner, DecisionChip, FinixLink, OutcomeChip, ScoreBadge, SignalList, Spinner } from '@/features/fraud/shared'
import { EMPTY, PILL, ROW, TD, TH, failureLabel, fmtDateTime, instrumentCount, kindLabel, moneyFormatter, relativeTime, userDisplayName } from '@/features/fraud/format'
import { CardBrand, cardText } from '@/features/fraud/card-brand'
import { CardChargesModal, RefundedBadge, type FraudChargedCard } from '@/features/fraud/card-charges-modal'

export const Route = createFileRoute('/_authenticated/events/$eventId/fraud_/users/$userId')({
  component: FraudUserPage,
})

type FeatureValue = string | number | boolean | null | undefined

/** The evidence panel, grouped so it reads top to bottom. */
const FEATURE_SECTIONS: { title: string; rows: [string, string][] }[] = [
  {
    title: 'This event',
    rows: [
      ['reload_count', 'Card charges here'],
      ['reload_amount', 'Charged here'],
      ['amount_24h', 'Charged in 24h'],
      ['reload_count_window', 'Charges in velocity window'],
      ['balance', 'Balance'],
    ],
  },
  {
    title: 'Cards',
    rows: [
      ['distinct_cards', 'Distinct cards (30 days)'],
      ['cards_all_time', 'Cards all time'],
      ['cards_added_24h', 'Cards added in 24h'],
      ['card_type', 'Card type'],
    ],
  },
  {
    title: 'Across accounts',
    rows: [
      ['shared_card_users', 'Accounts sharing a card'],
      ['fingerprint_charges_24h', 'Charges on the same card, 24h'],
    ],
  },
  {
    title: 'Declines and disputes',
    rows: [
      ['declines_recent', 'Provider declines, last hour'],
      ['declined_cards_recent', 'Cards those declines were on'],
      ['fraud_declines_24h', 'Issuer fraud declines, 24h'],
      ['risk_rule_declines_24h', 'Risk-rule declines, 24h'],
      ['chargebacks', 'Chargebacks on the account'],
    ],
  },
  {
    title: 'Transfers',
    rows: [['transfers_out_tokens', 'Tokens sent to others']],
  },
]

/** Nothing to say: zero, empty or missing. "0.00" from a Decimal counts as zero. */
function hasFeatureValue(value: FeatureValue): boolean {
  if (value == null || value === '' || value === false) return false
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const asNumber = Number(value)
    return Number.isNaN(asNumber) || asNumber !== 0
  }
  return true
}

/**
 * The features behind the score. Empty rows only get in the way of a triage,
 * so they hide behind "Show all"; a section with nothing to show is dropped.
 */
function EvidencePanel({ features, money }: {
  features: Record<string, string | number | boolean | null>
  money: (value: number | string | null | undefined) => string
}) {
  const [showAll, setShowAll] = useState(false)
  const present = (key: string) => key in features
  const hiddenCount = FEATURE_SECTIONS
    .reduce((total, section) => total + section.rows.filter(([key]) => present(key) && !hasFeatureValue(features[key])).length, 0)
  const sections = FEATURE_SECTIONS
    .map((section) => ({
      title: section.title,
      rows: section.rows.filter(([key]) => present(key) && (showAll || hasFeatureValue(features[key]))),
    }))
    .filter((section) => section.rows.length > 0)

  return (
    <div className="space-y-3">
      {sections.length === 0 && <p className="text-sm text-neutral-500 dark:text-neutral-400">Nothing recorded for this account yet.</p>}
      {sections.map((section) => (
        <div key={section.title}>
          <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{section.title}</h3>
          <dl className="mt-1 grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 text-sm">
            {section.rows.map(([key, label]) => (
              <div key={key} className="contents">
                <dt className="text-neutral-600 dark:text-neutral-400">{label}</dt>
                <dd className={cn(
                  'text-right tabular-nums text-neutral-900 dark:text-white',
                  !hasFeatureValue(features[key]) && 'text-neutral-400 dark:text-neutral-600',
                )}>
                  {key.includes('amount') ? money(String(features[key] ?? 0)) : String(features[key] ?? EMPTY)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          {showAll ? 'Show fewer' : `Show all (${hiddenCount} empty)`}
        </button>
      )}
    </div>
  )
}

function FraudUserPage() {
  const { eventId, userId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const userIdNum = Number(userId)
  const canViewFraud = usePermission('can_view_fraud')
  const canManage = usePermission('can_manage_fraud')
  const { data: event } = useEvent(eventIdNum)
  const { data, isLoading, error } = useFraudUser(eventIdNum, userIdNum)
  const { openUserModal, openTransactionModal } = useModal()
  const { user: viewer } = useAuth()
  // Finix ids are for staff, who can follow them; an organizer cannot.
  const isStaff = !!viewer?.is_superuser
  const [showBlock, setShowBlock] = useState(false)
  const [showTrust, setShowTrust] = useState(false)
  const trustCard = useCreateFraudAllowance(eventIdNum)
  const [cardError, setCardError] = useState<string | null>(null)
  const [blockingCard, setBlockingCard] = useState<FraudCardOnFile | null>(null)
  // The card whose charges are open in the modal, picked from a charge row.
  const [chargedCard, setChargedCard] = useState<FraudChargedCard | null>(null)

  // Permission guard - now safe after all hooks
  if (!canViewFraud) {
    return <AccessDenied message="You don't have permission to view fraud monitoring for this event." />
  }

  const money = moneyFormatter(event?.currency)

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-6 space-y-6">
      <Link to="/events/$eventId/fraud" params={{ eventId }} search={{ tab: 'users' }} className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline">
        <ArrowLeft className="w-4 h-4" /> Risk
      </Link>

      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">
          {(error as { status?: number }).status === 404 ? 'This account has no activity at this event.' : `Failed to load: ${error.message}`}
        </p>
      ) : isLoading || !data ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white flex items-center gap-3">
                {userDisplayName(data.user)} <ScoreBadge score={data.score} /> <DecisionChip decision={data.decision} />
                {data.trusted && <span className={cn(PILL, 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400')}>Trusted</span>}
              </h1>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                {data.user.email}{data.user.phone_number ? ` · ${data.user.phone_number}` : ''} · joined {fmtDateTime(data.user.date_joined)}
                {!data.user.is_active && <span className="ml-2 text-red-600">inactive</span>}
              </p>
              {data.block && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  Blocked ({data.block.scope}, {data.block.reason}{data.block.freeze_spending ? ', spending frozen' : ', cards only'}) since {fmtDateTime(data.block.created_at)}
                  {data.trusted ? ' · a block always wins: trust does not apply until it is lifted' : ''}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openUserModal(userIdNum, eventIdNum)}>Attendee profile</Button>
              {canManage && !data.trusted && (
                <Button variant="outline" size="sm" onClick={() => setShowTrust((v) => !v)}>{showTrust ? 'Cancel' : 'Trust'}</Button>
              )}
              {canManage && !data.block && (
                <Button variant="destructive" size="sm" onClick={() => setShowBlock((v) => !v)}>{showBlock ? 'Cancel' : 'Block'}</Button>
              )}
            </div>
          </div>

          {showBlock && canManage && (
            <Card>
              <CardHeader><CardTitle className="text-base">Block {userDisplayName(data.user)} at this event</CardTitle></CardHeader>
              <CardContent><BlockForm eventId={eventIdNum} userId={userIdNum} onDone={() => setShowBlock(false)} /></CardContent>
            </Card>
          )}

          {showTrust && canManage && (
            <Card>
              <CardHeader><CardTitle className="text-base">Trust {userDisplayName(data.user)} at this event</CardTitle></CardHeader>
              <CardContent><TrustForm eventId={eventIdNum} userId={userIdNum} activeBlock={data.block} onDone={() => setShowTrust(false)} /></CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Why this score</CardTitle></CardHeader>
              <CardContent><SignalList signals={data.signals} /></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Evidence</CardTitle></CardHeader>
              <CardContent><EvidencePanel features={data.features} money={money} /></CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Cards on file</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto px-0">
              {cardError && <div className="px-6 pb-3"><Banner kind="error">{cardError}</Banner></div>}
              {data.cards_on_file.length === 0 ? <p className="px-6 pb-4 text-sm text-neutral-500">No cards recorded at this organization's events.</p> : (
                <table className="w-full whitespace-nowrap">
                  <thead><tr className="border-b border-neutral-200 dark:border-neutral-800">
                    <th className={TH}>Card</th><th className={TH}>Type</th><th className={TH}>Issuer</th><th className={TH}>3DS verified</th><th className={TH}>Shared</th><th className={TH}>Added</th><th className={TH}>Trust / block</th>
                  </tr></thead>
                  <tbody>
                    {data.cards_on_file.map((c) => (
                      <tr key={c.id} className={cn(ROW, c.disabled_at && 'opacity-50')}>
                        <td className={TD}><CardBrand brand={c.brand} last4={c.last4} wallet={c.wallet} /><div className="text-xs text-neutral-500">{c.wallet ? `${instrumentCount(c.instruments)} · ` : ''}BIN {c.bin || EMPTY}</div></td>
                        <td className={TD}>{c.card_type || EMPTY}</td>
                        <td className={TD}>{c.issuer_country || EMPTY}</td>
                        <td className={TD}>{c.wallet ? <span className="text-neutral-500" title="Authenticated by the wallet on the device; never challenged">Wallet</span> : c.three_ds_verified_at ? fmtDateTime(c.three_ds_verified_at) : <span className="text-amber-600 dark:text-amber-400">No</span>}</td>
                        <td className={TD}>{c.shared_with_users ? <span className="text-amber-600 dark:text-amber-400">{c.shared_with_users} other account{c.shared_with_users === 1 ? '' : 's'}</span> : 'No'}</td>
                        <td className={TD}>{fmtDateTime(c.created_at)}{c.disabled_at && ' · removed'}</td>
                        <td className={TD}>
                          <div className="flex flex-wrap items-center gap-2">
                            {c.verification_state === 'failed' && (
                              <span
                                className={cn(PILL, 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400')}
                                title={`The issuer declined the $0 verification when this card was added${c.verification_failure_code ? ` (${c.verification_failure_code})` : ''}`}
                              >
                                Verification failed{c.verification_failure_code ? ` · ${failureLabel(c.verification_failure_code)}` : ''}
                              </span>
                            )}
                            {c.blocked && (
                              <span className={cn(PILL, 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400')} title="A card block beats a card trust">
                                Blocked{c.trusted ? ' · trust overridden' : ''}
                              </span>
                            )}
                            {!c.blocked && c.trusted && (
                              <span className={cn(PILL, 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400')}>Trusted</span>
                            )}
                            {canManage && !c.disabled_at && !c.blocked && !c.trusted && (
                              <button
                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                                disabled={trustCard.isPending}
                                onClick={async () => {
                                  setCardError(null)
                                  try {
                                    await trustCard.mutateAsync({ instrument_id: c.instrument_id, reason: 'Trusted from the attendee page' })
                                  } catch (err) {
                                    setCardError(apiErrorDetail(err, 'Could not trust this card.'))
                                  }
                                }}
                              >
                                Trust card
                              </button>
                            )}
                            {canManage && !c.disabled_at && !c.blocked && (
                              <button
                                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                                onClick={() => setBlockingCard(blockingCard?.id === c.id ? null : c)}
                              >
                                {blockingCard?.id === c.id ? 'Cancel' : 'Block card'}
                              </button>
                            )}
                            {canManage && c.blocked && c.block_id && data.blocks.find((b) => b.id === c.block_id)?.scope === 'event' && (
                              <LiftButton eventId={eventIdNum} block={{ id: c.block_id }} />
                            )}
                            {!canManage && !c.blocked && !c.trusted && EMPTY}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {blockingCard && canManage && (
                <div className="px-6 pt-4 pb-2 border-t border-neutral-100 dark:border-neutral-800">
                  <CardBlockForm
                    eventId={eventIdNum}
                    instrumentId={blockingCard.instrument_id}
                    cardLabel={cardText(blockingCard.brand, blockingCard.last4, blockingCard.wallet)}
                    onDone={() => setBlockingCard(null)}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Recent card charges</CardTitle></CardHeader>
              <CardContent className="overflow-x-auto px-0">
                {data.recent_reloads.length === 0 ? <p className="px-6 pb-4 text-sm text-neutral-500">None.</p> : (
                  <table className="w-full whitespace-nowrap">
                    <thead><tr className="border-b border-neutral-200 dark:border-neutral-800">
                      <th className={TH}>When</th><th className={TH}>Transaction</th><th className={TH}>Charged</th><th className={TH}>Card</th>
                      {isStaff && <th className={TH}>Transfer</th>}
                    </tr></thead>
                    <tbody>
                      {data.recent_reloads.map((r) => {
                        // An older backend sends only the normalized card key; a
                        // wallet payload key ("APPLE_PAY:1234") is not searchable.
                        const instrumentId = r.instrument_id || r.card || ''
                        const searchable = instrumentId.startsWith('PI')
                        const cardCell = r.card_brand || r.card_last4
                          ? <CardBrand brand={r.card_brand} last4={r.card_last4} wallet={r.card_wallet} />
                          : <span className="font-mono text-xs">{instrumentId || EMPTY}</span>
                        return (
                          <tr key={r.id} className={ROW}>
                            <td className={TD}>{fmtDateTime(r.timestamp)}</td>
                            <td className={TD}>
                              <button
                                type="button"
                                onClick={() => openTransactionModal(r.id, eventIdNum)}
                                className="text-blue-600 hover:underline dark:text-blue-400"
                                title="Open the transaction"
                              >
                                #{r.id}
                              </button>
                            </td>
                            <td className={TD}>
                              <span className="tabular-nums">{money(r.charged)}</span>
                              {r.refunded ? <span className="ml-1.5"><RefundedBadge /></span> : null}
                            </td>
                            <td className={TD}>
                              {searchable ? (
                                <button
                                  type="button"
                                  title="Every charge with this card"
                                  onClick={() => setChargedCard({
                                    instrumentId,
                                    brand: r.card_brand,
                                    last4: r.card_last4,
                                    wallet: r.card_wallet,
                                    cardType: data.cards_on_file.find((c) => c.instrument_id === instrumentId)?.card_type,
                                  })}
                                  className="rounded text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                >
                                  {cardCell}
                                </button>
                              ) : cardCell}
                            </td>
                            {isStaff && <td className={TD}><FinixLink id={r.provider_id} /></td>}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Decisions</CardTitle></CardHeader>
              <CardContent className="px-0">
                {data.assessments.length === 0 ? <p className="px-6 pb-4 text-sm text-neutral-500">None yet.</p> : (
                  <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                    {data.assessments.map((a) => (
                      <li key={a.id} className="px-6 py-2.5 text-sm">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <DecisionChip decision={a.decision} /><OutcomeChip outcome={a.outcome} />
                          <span className="text-neutral-600 dark:text-neutral-400">{kindLabel(a.kind)} · {money(a.amount)} · score {a.score}</span>
                          {a.failure_code && <span className="font-mono text-xs text-red-600" title={failureLabel(a.failure_code)}>{a.failure_code}</span>}
                          <span className="ml-auto text-xs text-neutral-500">{relativeTime(a.created_at)}</span>
                        </div>
                        {a.signals.length > 0 && <div className="mt-1.5"><SignalList signals={a.signals} compact /></div>}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Blocks</CardTitle></CardHeader>
              <CardContent className="px-0">
                {data.blocks.length === 0 ? <p className="px-6 pb-4 text-sm text-neutral-500">Never blocked here.</p> : (
                  <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                    {data.blocks.map((b) => (
                      <li key={b.id} className="px-6 py-2.5 text-sm flex flex-wrap items-center gap-3">
                        <span className={cn(PILL, b.active ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300')}>
                          {b.active ? 'Active' : b.lifted_at ? 'Lifted' : 'Expired'}
                        </span>
                        <span className="text-neutral-900 dark:text-white">
                          {b.kind === 'card' ? `card ${b.card ? cardText(b.card.brand, b.card.last4, b.card.wallet) : ''} · ` : ''}
                          {b.scope} · {b.reason}{b.kind === 'card' ? '' : b.freeze_spending ? '' : ' · cards only'}
                        </span>
                        <span className="text-xs text-neutral-500">{fmtDateTime(b.created_at)}{b.notes ? ` · ${b.notes}` : ''}{b.lift_notes ? ` · lifted: ${b.lift_notes}` : ''}</span>
                        {canManage && b.active && b.scope === 'event' && <span className="ml-auto"><LiftButton eventId={eventIdNum} block={b} /></span>}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Trusted</CardTitle></CardHeader>
              <CardContent className="px-0">
                {(data.allowances ?? []).length === 0 ? <p className="px-6 pb-4 text-sm text-neutral-500">Never trusted here. A trusted attendee or card is not scored: no 3DS challenge, no refusal, no transfer lock. Blocks still apply.</p> : (
                  <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                    {(data.allowances ?? []).map((a) => (
                      <li key={a.id} className="px-6 py-2.5 text-sm flex flex-wrap items-center gap-3">
                        <span className={cn(PILL, a.active ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300')}>
                          {a.active ? 'Active' : a.revoked_at ? 'Revoked' : 'Expired'}
                        </span>
                        <span className="text-neutral-900 dark:text-white">
                          {a.kind === 'attendee' ? 'Whole account' : `Card ${a.instrument_id || '(by fingerprint)'}`}
                        </span>
                        <span className="text-xs text-neutral-500">
                          {fmtDateTime(a.created_at)}{a.created_by ? ` · by ${userDisplayName(a.created_by)}` : ''}{a.expires_at ? ` · until ${fmtDateTime(a.expires_at)}` : ''}{a.reason ? ` · ${a.reason}` : ''}
                        </span>
                        {canManage && a.active && <span className="ml-auto"><RevokeButton eventId={eventIdNum} allowance={a} /></span>}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Chargebacks and transfers</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                {data.chargebacks.length === 0 ? <p className="text-neutral-500">No chargebacks at this organization's events.</p> : (
                  <ul className="space-y-1">
                    {data.chargebacks.map((c) => (
                      <li key={c.id} className="flex items-center gap-2">
                        <span className={cn(PILL, c.state === 'lost' || c.state === 'accepted' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' : c.state === 'won' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400')}>{c.state}</span>
                        <span>{money(c.amount)} · {c.reason || 'no reason code'} · {fmtDateTime(c.received_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {data.recent_transfers.length > 0 && (
                  <ul className="space-y-1 pt-2 border-t border-neutral-100 dark:border-neutral-800">
                    {data.recent_transfers.map((t) => (
                      <li key={t.id} className="text-neutral-700 dark:text-neutral-300">
                        {t.tokens < 0 ? `Sent ${Math.abs(t.tokens)} tokens to` : `Received ${t.tokens} tokens from`} user #{t.counterparty_id ?? '?'} · {fmtDateTime(t.timestamp)}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
          {chargedCard && (
            <CardChargesModal
              eventId={eventIdNum}
              card={chargedCard}
              money={money}
              onClose={() => setChargedCard(null)}
            />
          )}
        </>
      )}
    </div>
  )
}
